// "Create override…" / "Create A/B test…" for one document (spec/dashboard.md sections 6.1, 6.2 and 9): the parameter
// pick dialog, then the staged override or test, a toast and the LiveOps page focused on it. Shared by the documents
// grid's row menu, the document editor's Actions menu and the card actions (useDocumentActions).
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { Document } from '../../api/types';
import { routes } from '../../shell/routes';
import { useToasts } from '../../ui/Toast';
import { useWidgetEnv } from '../widgets/context';
import { createAbTestFor, createOverrideFor } from './create';
import { ParameterPickDialog } from './ParameterPickDialog';

export type CreateForKind = 'override' | 'abtest';

export interface CreateFor {
  /** opens the parameter dialog for the document; `beforeNavigate` runs once the item is staged (an editor closes itself) */
  open: (kind: CreateForKind, doc: Document, beforeNavigate?: () => void) => void;
  /** the open dialog, if any; render it once */
  dialog: ReactNode;
}

export function useCreateFor(): CreateFor {
  const { catalog, workspace, org, game, branch } = useWidgetEnv();
  const toasts = useToasts();
  const navigate = useNavigate();
  const [state, setState] = useState<{ kind: CreateForKind; doc: Document; beforeNavigate?: () => void } | null>(null);
  const dialog = state && (
    <ParameterPickDialog
      doc={state.doc}
      title={state.kind === 'override' ? 'Create override' : 'Create A/B test'}
      confirmLabel={state.kind === 'override' ? 'Create override (staged)' : 'Create A/B test (staged)'}
      onClose={() => setState(null)}
      onPick={(leaves) => {
        try {
          const env = { workspace, catalog };
          const page = state.kind === 'override' ? 'liveops/overrides' : 'liveops/ab-tests';
          const id = state.kind === 'override' ? createOverrideFor(env, state.doc, leaves) : createAbTestFor(env, state.doc, leaves);
          toasts.success(state.kind === 'override' ? 'Override staged; Save All commits it.' : 'A/B test staged (Inactive); Save All commits it, Run test starts it.');
          setState(null);
          state.beforeNavigate?.();
          navigate(`${routes.branch(org, game, branch, page)}?doc=${encodeURIComponent(id)}`);
        } catch (e) {
          toasts.error(e instanceof Error ? e.message : String(e));
        }
      }}
    />
  );
  return { open: (kind, doc, beforeNavigate) => setState({ kind, doc, beforeNavigate }), dialog };
}
